import { useState, useEffect } from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  TextField, 
  Button, 
  IconButton, 
  Tabs, 
  Tab, 
  InputAdornment, 
  Link,
  useMediaQuery,
  useTheme,
  Toolbar,
  AppBar,
  Menu,
  MenuItem,
  Divider,
  CircularProgress
} from "@mui/material";
import { Navigate, useNavigate, useLocation, Link as RouterLink } from "react-router-dom";
import { Visibility, VisibilityOff } from '@mui/icons-material';
import MenuIcon from '@mui/icons-material/Menu';
import { useAuthService } from '../services/authService';
import analyticsService from '../services/analyticsService';
import authConfig from '../config/auth.config';
import backdrop from "../img/backdrop.png";
import logo from '../img/signup-popcorn.png';
import headerLogo from '../img/headerlogo.png';
import Footer from '../components/Footer';

// Custom header for login page without login button
const LoginPageHeader = () => {
  const [anchorEl, setAnchorEl] = useState(null);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { isAuthenticated, signOut } = useAuthService();

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMenuClick = (path) => {
    navigate(path);
    handleClose();
  };

  const handleLogout = async () => {
    try {
      await signOut();
      navigate('/');
      // Force refresh of the page to ensure state is cleared
      window.location.reload();
    } catch (error) {
      console.error('Logout error:', error);
    }
    handleClose();
  };

  return (
    <AppBar 
      position="fixed" 
      sx={{ 
        backgroundColor: 'black', 
        boxShadow: isMobile ? '0px 2px 10px rgba(0, 0, 0, 0.5)' : 'none',
        zIndex: 1100, // Higher than footer to ensure proper stacking
        top: 0,
        left: 0,
        right: 0,
        width: '100%'
      }}
    >
      <Toolbar sx={{ justifyContent: 'space-between' }}>
        <Box
          component="img"
          src={headerLogo}
          alt="Screamify"
          sx={{
            height: { xs: '25px', sm: '30px' },
            cursor: 'pointer'
          }}
          onClick={() => navigate('/')}
        />
        
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton
            size="large"
            edge="end"
            color="inherit"
            aria-label="menu"
            onClick={handleMenu}
          >
            <MenuIcon />
          </IconButton>
        </Box>
        
        <Menu
          id="menu-appbar"
          anchorEl={anchorEl}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          keepMounted
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          open={Boolean(anchorEl)}
          onClose={handleClose}
          PaperProps={{
            sx: {
              backgroundColor: 'black',
              color: 'white',
              '& .MuiMenuItem-root:hover': {
                backgroundColor: 'rgba(255, 0, 0, 0.1)',
              },
            },
          }}
        >
          {/* Public menu items */}
          <MenuItem onClick={() => handleMenuClick('/shop')}>Shop</MenuItem>
          <MenuItem onClick={() => handleMenuClick('/about')}>About</MenuItem>
          <MenuItem onClick={() => handleMenuClick('/faq')}>FAQ</MenuItem>
          
          {isAuthenticated && (
            <>
              <Divider sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)' }} />
              <MenuItem onClick={handleLogout} sx={{ color: '#ff0000' }}>Logout</MenuItem>
            </>
          )}
        </Menu>
      </Toolbar>
    </AppBar>
  );
};

function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { isAuthenticated, signIn, signUp, forgotPassword, confirmSignUp, checkAuthState, signOut, clearExistingAuth } = useAuthService();
  
  // Get the confirmResetPassword function from Auth Service
  const confirmResetPassword = async (username, code, newPassword) => {
    // Use the default authService
    const authService = await import('../services/authService').then(module => module.default);
    return authService.confirmForgotPassword(username, code, newPassword);
  };

  // State
  const [activeTab, setActiveTab] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  
  // Form state
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [displayNameAvailable, setDisplayNameAvailable] = useState(null);
  const [isCheckingDisplayName, setIsCheckingDisplayName] = useState(false);
  const [code, setCode] = useState("");
  const [needsConfirmation, setNeedsConfirmation] = useState(false);
  
  // Password reset state
  const [resetPasswordMode, setResetPasswordMode] = useState(false);
  const [resetCode, setResetCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  
  // Parse URL params for email and activeTab
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const emailParam = urlParams.get('email');
    const activeTabParam = urlParams.get('activeTab');
    
    if (emailParam) {
      setEmail(emailParam);
    }
    
    if (activeTabParam !== null) {
      const tabIndex = parseInt(activeTabParam, 10);
      if (!isNaN(tabIndex) && (tabIndex === 0 || tabIndex === 1)) {
        setActiveTab(tabIndex);
      }
    }
  }, [location.search]);
  
  // If already authenticated, redirect to dashboard
  if (isAuthenticated) {
    navigate('/community');
    return null;
  }

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setError("");
    setNeedsConfirmation(false);
  };
  
  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };
  
  const checkDisplayNameAvailability = async (name) => {
    if (!name || name.length < 3) {
      setDisplayNameAvailable(null);
      return;
    }
    
    setIsCheckingDisplayName(true);
    try {
      console.log(`Checking availability for: "${name}"`);
      
      const response = await fetch('https://zm6jvy90l6.execute-api.us-east-1.amazonaws.com/scan-display-names', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ displayName: name })
      });
      
      if (!response.ok) {
        throw new Error(`API returned ${response.status}`);
      }
      
      const data = await response.json();
      console.log('Display name check API response:', data);
      console.log(`API says "${name}" is ${data.isAvailable ? 'AVAILABLE' : 'NOT AVAILABLE'}`);
      console.log('API message:', data.message);
      
      // Set display name availability based on the response
      setDisplayNameAvailable(data.isAvailable);
    } catch (error) {
      console.error('Error checking display name:', error);
      // Don't assume name is available on error - safer to treat as unavailable or unknown
      setDisplayNameAvailable(null);
    } finally {
      setIsCheckingDisplayName(false);
    }
  };
  
  const validateForm = () => {
    setError("");
    
    if (!email) {
      setError("Email is required");
      return false;
    }
    
    if (!password) {
      setError("Password is required");
      return false;
    }
    
    if (activeTab === 1 && password !== confirmPassword) {
      setError("Passwords do not match");
      return false;
    }
    
    if (activeTab === 1 && !displayName) {
      setError("Display name is required");
      return false;
    }
    
    if (activeTab === 1 && displayName.length < 3) {
      setError("Display name must be at least 3 characters");
      return false;
    }
    
    if (activeTab === 1 && displayNameAvailable === false) {
      setError("This display name is already taken");
      return false;
    }
    
    return true;
  };
  
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    
    // Validate email is not empty
    if (!email) {
      setError("Email is required");
      setLoading(false);
      return;
    }
    
    // Validate password is not empty
    if (!password) {
      setError("Password is required");
      setLoading(false);
      return;
    }
    
    try {
      console.log(`Attempting to sign in with email: ${email}`);
      await signIn(email, password);
      
      // Track successful login
      analyticsService.trackLogin('email');
      
      // Call checkAuthState to update the authentication state
      await checkAuthState();
      
      // Now redirect after auth state is updated
      navigate('/community');
    } catch (error) {
      setError(error.message || "Login failed. Please try again.");
      // Track login error
      analyticsService.trackError('login_error', error.message || 'Unknown login error');
    } finally {
      setLoading(false);
    }
  };
  
  const handleRegistration = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setLoading(true);
    setError("");
    
    try {
      // Attempt to sign up
      console.log('Attempting to sign up with:', { email, displayName });
      const signUpResult = await signUp(email, password, displayName);
      console.log('Sign up result:', signUpResult);
      
      // Check if user needs confirmation
      // This can come from multiple indicators in the result:
      // 1. signUpResult.userConfirmed === false (explicit)
      // 2. signUpResult.nextStep?.signUpStep === 'CONFIRM_SIGN_UP' (from Cognito)
      // 3. signUpResult.isSignUpComplete === false (another indicator)
      const needsConfirmationFlow = 
        signUpResult.userConfirmed === false || 
        (signUpResult.nextStep && signUpResult.nextStep.signUpStep === 'CONFIRM_SIGN_UP') ||
        signUpResult.isSignUpComplete === false;
      
      console.log('User needs confirmation?', needsConfirmationFlow);
      
      if (needsConfirmationFlow) {
        // User needs to confirm their email
        setNeedsConfirmation(true);
        // Track standard sign up event (will track the custom one after confirmation)
        analyticsService.trackSignUp('email');
      } else {
        // User was automatically confirmed
        // Track custom sign_up event with display_name
        analyticsService.trackEvent('sign_up', { 
          display_name: displayName 
        });
        // Also track standard sign up and login events
        analyticsService.trackSignUp('email');
        analyticsService.trackLogin('email');
        
        // Check if user is authenticated before redirecting
        if (isAuthenticated) {
          navigate('/community');
        }
      }
    } catch (error) {
      console.error('Registration error:', error);
      console.error('Error name:', error.name);
      console.error('Error message:', error.message);
      console.error('Error code:', error.code);
      console.error('Full error object:', JSON.stringify(error, null, 2));
      
      // Handle specific errors
      if (error.name === 'UsernameExistsException') {
        setError("An account with this email already exists. Please sign in.");
      } else {
        setError(error.message || "Registration failed. Please try again.");
      }
      
      // Track sign up error
      analyticsService.trackError('signup_error', error.message || 'Unknown signup error');
    } finally {
      setLoading(false);
    }
  };
  
  const handleConfirmation = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    
    try {
      await confirmSignUp(email, code);
      
      // After successful confirmation, attempt to sign in
      try {
        await signIn(email, password);
        
        // Track custom sign_up event with display_name
        analyticsService.trackEvent('sign_up', { 
          display_name: displayName 
        });
        
        // Also track login after confirmation
        analyticsService.trackLogin('email_confirmation');
        
        // Check authentication status before redirecting
        if (isAuthenticated) {
          navigate('/community');
        }
      } catch (signInError) {
        // Go back to login form if sign in fails
        setNeedsConfirmation(false);
        setActiveTab(0);
        setError("Verification successful. Please sign in.");
      }
    } catch (error) {
      setError(error.message || "Verification failed. Please try again.");
      // Track confirmation error
      analyticsService.trackError('confirmation_error', error.message || 'Unknown confirmation error');
    } finally {
      setLoading(false);
    }
  };
  
  const handleForgotPassword = async () => {
    if (!email) {
      setError("Please enter your email first");
      return;
    }
    
    try {
      console.log(`Attempting password reset for email: ${email}`);
      await forgotPassword(email);
      console.log(`Password reset initiated for: ${email}`);
      setError("Password reset instructions sent to your email");
      setResetPasswordMode(true);
    } catch (error) {
      console.error('Forgot password error:', error);
      console.error('Error name:', error.name);
      console.error('Error message:', error.message);
      if (error.name === 'UserNotFoundException') {
        setError("We couldn't find an account with that email. Please check the email or create a new account.");
      } else {
        setError(error.message || "Failed to request password reset. Please try again.");
      }
    }
  };

  const handleConfirmResetPassword = async (e) => {
    e.preventDefault();
    
    if (!resetCode) {
      setError("Please enter the verification code sent to your email");
      return;
    }
    
    if (!newPassword) {
      setError("Please enter a new password");
      return;
    }
    
    if (newPassword !== confirmNewPassword) {
      setError("Passwords do not match");
      return;
    }
    
    setLoading(true);
    setError("");
    
    try {
      console.log(`Confirming password reset for email: ${email}`);
      await confirmResetPassword(email, resetCode, newPassword);
      setError("Password reset successful. You can now log in with your new password.");
      setResetPasswordMode(false);
      // Clear the password fields
      setPassword("");
      setNewPassword("");
      setConfirmNewPassword("");
      setResetCode("");
    } catch (error) {
      console.error('Password reset confirmation error:', error);
      if (error.name === 'CodeMismatchException') {
        setError("Invalid verification code. Please check and try again.");
      } else if (error.name === 'InvalidPasswordException') {
        setError(error.message || "Password does not meet requirements.");
      } else {
        setError(error.message || "Failed to reset password. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box 
      sx={{ 
        width: '100%', 
        minHeight: '100vh',
        backgroundImage: `url(${backdrop})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        position: 'relative',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <LoginPageHeader />
      
      {/* Toolbar for spacing below fixed AppBar */}
      <Toolbar />
      
      <Box 
        sx={{
          flex: 1,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          width: '100%',
          position: 'relative',
          zIndex: 1,
          py: 0,
          minHeight: { xs: 'calc(100vh - 120px)', md: 'calc(100vh - 140px)' },
          ...(isMobile && {
            pt: 2,
            pb: 2,
          })
        }}
      >
        <Container 
          maxWidth="xs" 
          sx={{ 
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            px: 2,
            py: 0,
            mx: 'auto'
          }}
        >
          <Box 
            sx={{ 
              width: '100%',
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              backdropFilter: 'blur(10px)',
              borderRadius: '12px',
              boxShadow: '0 4px 30px rgba(0, 0, 0, 0.2)',
              overflow: 'hidden',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              position: 'relative'
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                pt: 4,
                pb: 3,
                position: 'relative',
                zIndex: 2
              }}
            >
              <Box 
                component="img"
                src={logo}
                alt="Screamify Logo"
                sx={{
                  height: "120px", width: "auto", objectFit: "contain", mt: 4
                }}
              />
            </Box>


            <Box sx={{ p: { xs: 3, sm: 4 } }}>
              {/* Sign In Form */}
              {!needsConfirmation && !resetPasswordMode && (
                <Box component="form" onSubmit={handleLogin} noValidate>
                  <TextField
                    variant="outlined"
                    fullWidth
                    placeholder="Enter your Email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    margin="dense"
                    autoComplete="email"
                    inputProps={{ 
                      style: { 
                        color: '#000000',
                        height: '24px',
                        padding: '10px 14px'
                      } 
                    }}
                    sx={{
                      mb: 2,
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                        backdropFilter: 'blur(5px)',
                        borderRadius: '6px',
                        transition: 'all 0.3s ease',
                        '& fieldset': {
                          borderColor: 'rgba(255, 255, 255, 0.2)',
                        },
                        '&:hover fieldset': {
                          borderColor: 'rgba(255, 255, 255, 0.3)',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#ff0000',
                          borderWidth: '2px',
                          boxShadow: '0 0 10px rgba(255, 0, 0, 0.2)'
                        },
                      },
                      '& input::placeholder': {
                        color: 'rgba(0, 0, 0, 0.6)',
                        opacity: 1
                      }
                    }}
                  />
                  <TextField
                    variant="outlined"
                    fullWidth
                    placeholder="Enter your Password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    margin="dense"
                    autoComplete="current-password"
                    InputProps={{
                      style: { 
                        color: '#000000'
                      },
                      sx: {
                        height: '44px'
                      },
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={togglePasswordVisibility}
                            edge="end"
                            sx={{ 
                              color: 'rgba(0, 0, 0, 0.7)',
                              '&:hover': {
                                color: '#ff0000'
                              }
                            }}
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                    inputProps={{
                      style: {
                        color: '#000000',
                        height: '24px',
                        padding: '10px 14px'
                      }
                    }}
                    sx={{
                      mb: 3,
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                        backdropFilter: 'blur(5px)',
                        borderRadius: '6px',
                        transition: 'all 0.3s ease',
                        '& fieldset': {
                          borderColor: 'rgba(255, 255, 255, 0.2)',
                        },
                        '&:hover fieldset': {
                          borderColor: 'rgba(255, 255, 255, 0.3)',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#ff0000',
                          borderWidth: '2px',
                          boxShadow: '0 0 10px rgba(255, 0, 0, 0.2)'
                        },
                      },
                      '& input::placeholder': {
                        color: 'rgba(0, 0, 0, 0.6)',
                        opacity: 1
                      }
                    }}
                  />
                  
                  {error && (
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: '#ff5555', 
                        mb: 2,
                        textAlign: 'center',
                        backgroundColor: 'rgba(255, 0, 0, 0.1)',
                        borderRadius: '4px',
                        p: 1,
                        border: '1px solid rgba(255, 0, 0, 0.2)'
                      }}
                    >
                      {error}
                    </Typography>
                  )}
                  
                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    disabled={loading}
                    sx={{
                      mt: 1,
                      mb: 3,
                      py: { xs: 1.5, sm: 1.8 },
                      backgroundColor: '#ff0000',
                      backgroundImage: 'linear-gradient(45deg, #ff0000, #cc0000)',
                      '&:hover': {
                        backgroundColor: '#cc0000',
                        backgroundImage: 'linear-gradient(45deg, #cc0000, #990000)',
                        boxShadow: '0 0 20px rgba(255, 0, 0, 0.3)'
                      },
                      '&:active': {
                        backgroundColor: '#990000',
                        transform: 'scale(0.98)'
                      },
                      fontWeight: 'bold',
                      fontSize: { xs: '1rem', sm: '1.1rem' },
                      textTransform: 'none',
                      borderRadius: '6px',
                      boxShadow: '0 2px 8px rgba(255, 0, 0, 0.25)',
                      letterSpacing: '0.5px',
                      border: '1px solid rgba(255, 0, 0, 0.3)'
                    }}
                  >
                    {loading ? 'Signing in...' : 'Sign in'}
                  </Button>
                  
                  <Box sx={{ textAlign: 'center' }}>
                    <Link
                      component="button"
                      variant="body2"
                      onClick={handleForgotPassword}
                      sx={{
                        color: 'rgba(255, 255, 255, 0.7)',
                        textDecoration: 'none',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          color: '#ff0000',
                          textDecoration: 'none',
                          textShadow: '0 0 10px rgba(255, 0, 0, 0.3)'
                        }
                      }}
                    >
                      Forgot your password?
                    </Link>
                  </Box>
                  
                </Box>
              )}
              
              {/* Password Reset Form */}
              {resetPasswordMode && (
                <Box component="form" onSubmit={handleConfirmResetPassword} noValidate>
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      color: 'rgba(255, 255, 255, 0.9)', 
                      textAlign: 'center',
                      mb: 2
                    }}
                  >
                    Reset Your Password
                  </Typography>
                  
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: 'rgba(255, 255, 255, 0.7)', 
                      textAlign: 'center',
                      mb: 3
                    }}
                  >
                    Enter the verification code sent to your email and create a new password.
                  </Typography>
                  
                  <TextField
                    variant="outlined"
                    fullWidth
                    placeholder="Verification Code"
                    value={resetCode}
                    onChange={(e) => setResetCode(e.target.value)}
                    margin="dense"
                    inputProps={{ 
                      style: { 
                        color: '#000000',
                        height: '24px',
                        padding: '10px 14px'
                      } 
                    }}
                    sx={{
                      mb: 2,
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                        backdropFilter: 'blur(5px)',
                        borderRadius: '6px',
                        transition: 'all 0.3s ease',
                        '& fieldset': {
                          borderColor: 'rgba(255, 255, 255, 0.2)',
                        },
                        '&:hover fieldset': {
                          borderColor: 'rgba(255, 255, 255, 0.3)',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#ff0000',
                          borderWidth: '2px',
                          boxShadow: '0 0 10px rgba(255, 0, 0, 0.2)'
                        },
                      },
                      '& input::placeholder': {
                        color: 'rgba(0, 0, 0, 0.6)',
                        opacity: 1
                      }
                    }}
                  />
                  
                  <TextField
                    variant="outlined"
                    fullWidth
                    placeholder="New Password"
                    type={showPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    margin="dense"
                    InputProps={{
                      style: { 
                        color: '#000000'
                      },
                      sx: {
                        height: '44px'
                      },
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={togglePasswordVisibility}
                            edge="end"
                            sx={{ 
                              color: 'rgba(0, 0, 0, 0.7)',
                              '&:hover': {
                                color: '#ff0000'
                              }
                            }}
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                    inputProps={{
                      style: {
                        color: '#000000',
                        height: '24px',
                        padding: '10px 14px'
                      }
                    }}
                    sx={{
                      mb: 2,
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                        backdropFilter: 'blur(5px)',
                        borderRadius: '6px',
                        transition: 'all 0.3s ease',
                        '& fieldset': {
                          borderColor: 'rgba(255, 255, 255, 0.2)',
                        },
                        '&:hover fieldset': {
                          borderColor: 'rgba(255, 255, 255, 0.3)',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#ff0000',
                          borderWidth: '2px',
                          boxShadow: '0 0 10px rgba(255, 0, 0, 0.2)'
                        },
                      },
                      '& input::placeholder': {
                        color: 'rgba(0, 0, 0, 0.6)',
                        opacity: 1
                      }
                    }}
                  />
                  
                  <TextField
                    variant="outlined"
                    fullWidth
                    placeholder="Confirm New Password"
                    type={showPassword ? 'text' : 'password'}
                    value={confirmNewPassword}
                    onChange={(e) => setConfirmNewPassword(e.target.value)}
                    margin="dense"
                    InputProps={{
                      style: { 
                        color: '#000000'
                      },
                      sx: {
                        height: '44px'
                      },
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={togglePasswordVisibility}
                            edge="end"
                            sx={{ 
                              color: 'rgba(0, 0, 0, 0.7)',
                              '&:hover': {
                                color: '#ff0000'
                              }
                            }}
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                    inputProps={{
                      style: {
                        color: '#000000',
                        height: '24px',
                        padding: '10px 14px'
                      }
                    }}
                    sx={{
                      mb: 3,
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                        backdropFilter: 'blur(5px)',
                        borderRadius: '6px',
                        transition: 'all 0.3s ease',
                        '& fieldset': {
                          borderColor: 'rgba(255, 255, 255, 0.2)',
                        },
                        '&:hover fieldset': {
                          borderColor: 'rgba(255, 255, 255, 0.3)',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#ff0000',
                          borderWidth: '2px',
                          boxShadow: '0 0 10px rgba(255, 0, 0, 0.2)'
                        },
                      },
                      '& input::placeholder': {
                        color: 'rgba(0, 0, 0, 0.6)',
                        opacity: 1
                      }
                    }}
                  />
                  
                  {error && (
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: '#ff5555', 
                        mb: 2,
                        textAlign: 'center',
                        backgroundColor: 'rgba(255, 0, 0, 0.1)',
                        borderRadius: '4px',
                        p: 1,
                        border: '1px solid rgba(255, 0, 0, 0.2)'
                      }}
                    >
                      {error}
                    </Typography>
                  )}
                  
                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    disabled={loading}
                    sx={{
                      mt: 1,
                      mb: 2,
                      py: { xs: 1.5, sm: 1.8 },
                      backgroundColor: '#ff0000',
                      backgroundImage: 'linear-gradient(45deg, #ff0000, #cc0000)',
                      '&:hover': {
                        backgroundColor: '#cc0000',
                        backgroundImage: 'linear-gradient(45deg, #cc0000, #990000)',
                        boxShadow: '0 0 20px rgba(255, 0, 0, 0.3)'
                      },
                      '&:active': {
                        backgroundColor: '#990000',
                        transform: 'scale(0.98)'
                      },
                      fontWeight: 'bold',
                      fontSize: { xs: '1rem', sm: '1.1rem' },
                      textTransform: 'none',
                      borderRadius: '6px',
                      boxShadow: '0 2px 8px rgba(255, 0, 0, 0.25)',
                      letterSpacing: '0.5px',
                      border: '1px solid rgba(255, 0, 0, 0.3)'
                    }}
                  >
                    {loading ? 'Resetting...' : 'Reset Password'}
                  </Button>
                  
                  <Box sx={{ textAlign: 'center', mt: 1 }}>
                    <Link
                      component="button"
                      variant="body2"
                      onClick={() => setResetPasswordMode(false)}
                      sx={{
                        color: 'rgba(255, 255, 255, 0.7)',
                        textDecoration: 'none',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          color: '#ff0000',
                          textDecoration: 'none',
                          textShadow: '0 0 10px rgba(255, 0, 0, 0.3)'
                        }
                      }}
                    >
                      Back to Sign In
                    </Link>
                  </Box>
                </Box>
              )}
              
              {/* Sign Up Form */}
              {activeTab === 1 && !needsConfirmation && !resetPasswordMode && (
                <Box component="form" onSubmit={handleRegistration} noValidate>
                  <TextField
                    variant="outlined"
                    fullWidth
                    placeholder="Enter your Email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    margin="dense"
                    autoComplete="email"
                    inputProps={{ 
                      style: { 
                        color: '#000000',
                        height: '24px',
                        padding: '10px 14px'
                      } 
                    }}
                    sx={{
                      mb: 2,
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                        backdropFilter: 'blur(5px)',
                        borderRadius: '6px',
                        transition: 'all 0.3s ease',
                        '& fieldset': {
                          borderColor: 'rgba(255, 255, 255, 0.2)',
                        },
                        '&:hover fieldset': {
                          borderColor: 'rgba(255, 255, 255, 0.3)',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#ff0000',
                          borderWidth: '2px',
                          boxShadow: '0 0 10px rgba(255, 0, 0, 0.2)'
                        },
                      },
                      '& input::placeholder': {
                        color: 'rgba(0, 0, 0, 0.6)',
                        opacity: 1
                      }
                    }}
                  />
                  
                  {/* Display Name Field with Label */}
                  <Typography 
                    variant="subtitle2" 
                    sx={{ 
                      fontWeight: 'bold', 
                      color: 'rgba(255, 255, 255, 0.9)', 
                      mt: 1,
                      ml: 1,
                      display: 'flex',
                      alignItems: 'center'
                    }}
                  >
                    <span style={{ color: '#ff0000', marginRight: '4px' }}>*</span> Display Name (required):
                  </Typography>
                  <TextField
                    variant="outlined"
                    fullWidth
                    placeholder="Choose a display name"
                    value={displayName}
                    onChange={(e) => {
                      const value = e.target.value;
                      setDisplayName(value);
                      if (value.length >= 3) {
                        checkDisplayNameAvailability(value);
                      } else {
                        setDisplayNameAvailable(null);
                      }
                    }}
                    margin="dense"
                    autoComplete="username"
                    InputProps={{
                      style: { 
                        color: '#000000'
                      },
                      sx: {
                        height: '44px'
                      },
                      endAdornment: (
                        <InputAdornment position="end">
                          {isCheckingDisplayName && (
                            <CircularProgress size={20} thickness={5} sx={{ color: 'rgba(0, 0, 0, 0.5)' }} />
                          )}
                          {!isCheckingDisplayName && displayNameAvailable === true && (
                            <Box 
                              component="span" 
                              sx={{ 
                                color: 'green', 
                                fontWeight: 'bold',
                                fontSize: '0.75rem'
                              }}
                            >
                              Available
                            </Box>
                          )}
                          {!isCheckingDisplayName && displayNameAvailable === false && (
                            <Box 
                              component="span" 
                              sx={{ 
                                color: 'red', 
                                fontWeight: 'bold',
                                fontSize: '0.75rem'
                              }}
                            >
                              Taken
                            </Box>
                          )}
                        </InputAdornment>
                      )
                    }}
                    inputProps={{ 
                      style: { 
                        color: '#000000',
                        height: '24px',
                        padding: '10px 14px'
                      } 
                    }}
                    sx={{
                      mb: 2,
                      mt: 2,
                      border: '2px dashed rgba(255, 0, 0, 0.4)',
                      borderRadius: '8px',
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'rgba(255, 255, 255, 0.95)',
                        backdropFilter: 'blur(5px)',
                        borderRadius: '6px',
                        transition: 'all 0.3s ease',
                        '& fieldset': {
                          borderColor: displayNameAvailable === false ? 'red' : 
                                      displayNameAvailable === true ? 'green' : 
                                      '#ff0000',
                          borderWidth: '2px',
                        },
                        '&:hover fieldset': {
                          borderColor: 'rgba(255, 0, 0, 0.7)',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: displayNameAvailable === false ? 'red' : 
                                       displayNameAvailable === true ? 'green' : 
                                       '#ff0000',
                          borderWidth: '3px',
                          boxShadow: '0 0 15px rgba(255, 0, 0, 0.3)'
                        },
                      },
                      '& input::placeholder': {
                        color: 'rgba(0, 0, 0, 0.8)',
                        fontWeight: 'bold',
                        opacity: 1
                      }
                    }}
                  />
                  
                  <TextField
                    variant="outlined"
                    fullWidth
                    placeholder="Enter your Password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    margin="dense"
                    autoComplete="new-password"
                    InputProps={{
                      style: { 
                        color: '#000000'
                      },
                      sx: {
                        height: '44px'
                      },
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={togglePasswordVisibility}
                            edge="end"
                            sx={{ 
                              color: 'rgba(0, 0, 0, 0.7)',
                              '&:hover': {
                                color: '#ff0000'
                              }
                            }}
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                    inputProps={{
                      style: {
                        color: '#000000',
                        height: '24px',
                        padding: '10px 14px'
                      }
                    }}
                    sx={{
                      mb: 2,
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                        backdropFilter: 'blur(5px)',
                        borderRadius: '6px',
                        transition: 'all 0.3s ease',
                        '& fieldset': {
                          borderColor: 'rgba(255, 255, 255, 0.2)',
                        },
                        '&:hover fieldset': {
                          borderColor: 'rgba(255, 255, 255, 0.3)',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#ff0000',
                          borderWidth: '2px',
                          boxShadow: '0 0 10px rgba(255, 0, 0, 0.2)'
                        },
                      },
                      '& input::placeholder': {
                        color: 'rgba(0, 0, 0, 0.6)',
                        opacity: 1
                      }
                    }}
                  />
                  
                  <TextField
                    variant="outlined"
                    fullWidth
                    placeholder="Confirm your Password"
                    type={showPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    margin="dense"
                    autoComplete="new-password"
                    InputProps={{
                      style: { 
                        color: '#000000'
                      },
                      sx: {
                        height: '44px'
                      },
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={togglePasswordVisibility}
                            edge="end"
                            sx={{ 
                              color: 'rgba(0, 0, 0, 0.7)',
                              '&:hover': {
                                color: '#ff0000'
                              }
                            }}
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                    inputProps={{
                      style: {
                        color: '#000000',
                        height: '24px',
                        padding: '10px 14px'
                      }
                    }}
                    sx={{
                      mb: 3,
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                        backdropFilter: 'blur(5px)',
                        borderRadius: '6px',
                        transition: 'all 0.3s ease',
                        '& fieldset': {
                          borderColor: 'rgba(255, 255, 255, 0.2)',
                        },
                        '&:hover fieldset': {
                          borderColor: 'rgba(255, 255, 255, 0.3)',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#ff0000',
                          borderWidth: '2px',
                          boxShadow: '0 0 10px rgba(255, 0, 0, 0.2)'
                        },
                      },
                      '& input::placeholder': {
                        color: 'rgba(0, 0, 0, 0.6)',
                        opacity: 1
                      }
                    }}
                  />
                  
                  {error && (
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: '#ff5555', 
                        mb: 2,
                        textAlign: 'center',
                        backgroundColor: 'rgba(255, 0, 0, 0.1)',
                        borderRadius: '4px',
                        p: 1,
                        border: '1px solid rgba(255, 0, 0, 0.2)'
                      }}
                    >
                      {error}
                    </Typography>
                  )}
                  
                </Box>
              )}
              
              {/* Verification Code Form */}
              {needsConfirmation && !resetPasswordMode && (
                <Box component="form" onSubmit={handleConfirmation} noValidate>
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      color: 'rgba(255, 255, 255, 0.9)',
                      mb: 2,
                      textAlign: 'center',
                      fontWeight: 500,
                      letterSpacing: '0.5px'
                    }}
                  >
                    We sent a verification code to your email
                  </Typography>
                  
                  <TextField
                    variant="outlined"
                    fullWidth
                    placeholder="Enter verification code"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    margin="dense"
                    inputProps={{ 
                      style: { 
                        color: '#000000',
                        height: '24px',
                        padding: '10px 14px'
                      } 
                    }}
                    sx={{
                      mb: 3,
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                        backdropFilter: 'blur(5px)',
                        borderRadius: '6px',
                        transition: 'all 0.3s ease',
                        '& fieldset': {
                          borderColor: 'rgba(255, 255, 255, 0.2)',
                        },
                        '&:hover fieldset': {
                          borderColor: 'rgba(255, 255, 255, 0.3)',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#ff0000',
                          borderWidth: '2px',
                          boxShadow: '0 0 10px rgba(255, 0, 0, 0.2)'
                        },
                      },
                      '& input::placeholder': {
                        color: 'rgba(0, 0, 0, 0.6)',
                        opacity: 1
                      }
                    }}
                  />
                  
                  {error && (
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: '#ff5555', 
                        mb: 2,
                        textAlign: 'center',
                        backgroundColor: 'rgba(255, 0, 0, 0.1)',
                        borderRadius: '4px',
                        p: 1,
                        border: '1px solid rgba(255, 0, 0, 0.2)'
                      }}
                    >
                      {error}
                    </Typography>
                  )}
                  
                </Box>
              )}
            </Box>
          </Box>
        </Container>
      </Box>
      
      <Footer />
    </Box>
  );
}

export default Login;